import React, { FC } from 'react';
import { get } from 'lodash';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@apollo/client';
import { useNavigate, useParams } from 'react-router-dom';

import Button from 'vibo-ui/Button';
import { AddToTemplatesButton } from 'components/timeline/TimelineInfo';

import { useModal } from 'vibo-ui/Modal';
import { onError } from 'graphql/helpers';
import { useMe } from 'graphql/hooks/user';
import { toastNotify } from 'graphql/hooks/common';
import { useEvent } from 'components/context/EventContext';
import { MAX_EVENT_TITLE_LENGTH } from 'services/constants';
import { updateTemplatePublic } from 'graphql/cache/templates';
import { copyToClipboard } from 'services/common/stringHelpers';

import { AllTimelineGqlOptions, useTimeline } from 'components/events/Timeline/TimelineContext';

import { GET_TEMPLATE_DEEP_LINK } from 'graphql/queries/template';
import {
  CLONE_TEMPLATE,
  SET_TEMPLATE_DEFAULT,
  TOGGLE_PUBLIC_TEMPLATE,
} from 'graphql/mutations/template';

import { IconmoonFont } from 'vibo-ui/Icon';
import { ButtonIconType, Modals } from 'types/enums';
import { CloneModalProps } from 'components/modals/CloneModal';
import { ShareModalProps } from 'components/modals/ShareModal';
import { EventSettingsModalProps } from 'components/modals/EventSettingsModal';

import useSettingsDropdownStyles from 'components/timeline/TimelineInfo/EventInfoCard/style';

const TimelineTools: FC = () => {
  const settingsDropdownClasses = useSettingsDropdownStyles();
  const { t } = useTranslation();

  const { id: templateId = '' } = useParams<keyof EventPageRouteParams>();
  const navigate = useNavigate();
  const eventContext = useEvent();
  const timeline = useTimeline<AllTimelineGqlOptions>();
  const { openModal } = useModal();
  const { isAdmin } = useMe();

  const template = (eventContext.event as unknown) as Template;

  const openCloneModal = () => {
    openModal<CloneModalProps>({
      key: Modals.clone,
      props: {
        values: {
          title: template.title,
        },
        onSubmit: payload =>
          cloneTemplate?.({
            variables: {
              templateId,
              payload,
            },
          }),
        maxLength: MAX_EVENT_TITLE_LENGTH,
        title: t('cloneTemplate'),
        label: t('templateName'),
        closeOnSubmit: true,
      },
    });
  };
  const openShareModal = () =>
    openModal<ShareModalProps>({
      key: Modals.share,
      props: {
        mutation: TOGGLE_PUBLIC_TEMPLATE,
        variables: {
          templateId: template?._id,
        },
        onCompleted: () => {
          !!template?._id && updateTemplatePublic(template);
        },
        option: template,
        title: t('shareTemplate'),
        children: t(
          template?.isPublic ? 'makeTemplatePrivateModalText' : 'makeTemplatePublicModalText'
        ),
      },
    });
  const openTemplateSettingsModal = () =>
    openModal<EventSettingsModalProps>({
      key: Modals.eventSettings,
      props: {
        eventContext,
        variables: { templateId },
        mutation: timeline.event.mutations.update.mutation,
      },
    });

  const templateDeepLinkData = useQuery(GET_TEMPLATE_DEEP_LINK, {
    variables: {
      templateId,
    },
    onError,
  });
  const templateDeepLink = get(templateDeepLinkData.data, 'getTemplateDeepLink');

  const [cloneTemplate] = useMutation(CLONE_TEMPLATE, {
    onCompleted: data => {
      toastNotify({ text: t('templateClonedSuccessfully'), iconType: ButtonIconType.clone });
      navigate(`/template/${data.cloneTemplate._id}/timeline`);
    },
  });
  const [setTemplateDefault, { loading: isMakingDefault }] = useMutation(SET_TEMPLATE_DEFAULT, {
    onCompleted: data => {
      timeline.event.mutations.update.updateCache?.(data.setTemplateDefault);
    },
  });

  const goNotificationsPage = () => navigate(`/template/${templateId}/notifications`);

  const handleGetTemplateDeepLink = () => {
    copyToClipboard(templateDeepLink, t('templateLinkCopiedClipboardCanShareWithViboDjs'));
  };

  const toggleTemplateDefault = () => {
    setTemplateDefault({
      variables: {
        templateId,
        isDefault: !template.isDefault,
      },
    });
  };

  return (
    <div className={settingsDropdownClasses.timelineTools}>
      <div className="extraTools">
        <AddToTemplatesButton />
        {isAdmin && template.canEdit ? (
          <Button
            onClick={toggleTemplateDefault}
            disabled={isMakingDefault}
            prefixIcon={IconmoonFont['templates-24']}
            displayType="bordered"
            shape="round"
            size="lg"
          >
            {template.isDefault ? 'Remove from defaults' : 'Make default'}
          </Button>
        ) : null}
        {template.isPublic && !eventContext.isEditable ? null : (
          <>
            <Button
              onClick={openCloneModal}
              prefixIcon={IconmoonFont['plus-24']}
              displayType="bordered"
              shape="round"
              size="lg"
            >
              {t('cloneTemplate')}
            </Button>
            <Button
              onClick={handleGetTemplateDeepLink}
              prefixIcon={IconmoonFont['share-arrow-24']}
              displayType="bordered"
              shape="round"
              size="lg"
            >
              {t('shareTemplate')}
            </Button>
            <Button
              onClick={openShareModal}
              prefixIcon={IconmoonFont['publicOutline-24']}
              displayType="bordered"
              shape="round"
              size="lg"
            >
              {t(template.isPublic ? 'makePrivate' : 'makePublic')}
            </Button>
          </>
        )}
      </div>
      <Button
        onClick={goNotificationsPage}
        tooltipProps={{
          overlay: 'Edit push notifications',
          fillOverlay: true,
          placement: 'top',
        }}
        prefixIcon={IconmoonFont['editPush-24']}
        displayType="white"
        shape="round"
        size="xl"
      />
      <Button
        onClick={openTemplateSettingsModal}
        prefixIcon={IconmoonFont['editPencil-24']}
        tooltipProps={{
          overlay: 'Edit template settings',
          fillOverlay: true,
          placement: 'top',
        }}
        displayType="white"
        shape="round"
        size="xl"
      />
    </div>
  );
};

export default TimelineTools;
